import * as React from 'react';
import PropTypes from 'prop-types';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { IconButton, Typography, useAutocomplete } from '@mui/material';
import { ordenarOpcionesPorLabel, textoMayusculas } from '../utils';

const Root = styled('div')(
  ({ theme }) => `
  color: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,.85)'};
  font-size: 16px;
  position: relative;
`,
);

const InputWrapper = styled('div')(
  ({ theme }) => `
  width: 300px;
  border: 1px solid #adadad;
  background-color: #ffffff08;
  border-radius: 4px;
  padding: 1px;
  display: flex;
  flex-wrap: wrap;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--secundario);
    transition: all 0.3s ease;
  }

  &.focused {
    border: 2px solid var(--principal);
    transition: all 0.3s ease;
  }

  & input {
    background-color: transparent;
    color: black;
    height: 52px;
    box-sizing: border-box;
    padding: 16px 12px 4px 12px;
    width: 0;
    min-width: 30px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    outline: 0;
    transition: all 0.3s ease;
    z-index: 2;
  }

  // Estilos para el label cuando hay valor o está enfocado
  &.has-value .floating-label,
  &.focused .floating-label {
    top: -3px;
    left: 8px;
    font-size: 12px;
    color: var(--principal);
    background-color: white;
    padding: 0 8px;
    transition: all 0.3s ease;
  }

  // Solo para el estado focused sin valor
  &.focused:not(.has-value) .floating-label {
    top: -3px;
    left: 8px;
    font-size: 12px;
    color: var(--principal);
    background-color: white;
    padding: 0 8px;
    transition: all 0.3s ease;
  }
`,
);

// Componente del label flotante
const FloatingLabel = styled('label')`
  position: absolute;
  left: 12px;
  top: 47%;
  transform: translateY(-50%);
  color: #666;
  font-size: 16px;
  pointer-events: none;
  transition: all 0.3s ease;
  z-index: 1;
  background: transparent;
  padding: 0 4px;
`;

function Tag(props) {
  const { label, onDelete, ...other } = props;
  return (
    <div {...other}>
      <span>{label}</span>
      <IconButton size='small'>
        <CloseIcon onClick={onDelete} />
      </IconButton>
    </div>
  );
}

Tag.propTypes = {
  label: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const StyledTag = styled(Tag)(
  ({ theme }) => `
  display: flex;
  align-items: center;
  height: 24px;
  margin: 2px;
  line-height: 22px;
  background-color: #edededff;
  border: 1px solid var(--principal);
  border-radius: 2px;
  box-sizing: content-box;
  padding: 0 4px 0 10px;
  outline: 0;
  overflow: hidden;
  transition: all 0.3s ease;
  margin-top: 8px; // Ajuste del margen superior

  &:focus {
    border-color: var(--secundario);
    background-color: var(--fondo2);
    transition: all 0.3s ease;
  }

  & span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    transition: all 0.3s ease;
  }

  & svg {
    font-size: 14px;
    cursor: pointer;
    padding: 4px;
    transition: all 0.3s ease;
  }
`,
);

const Listbox = styled('ul')(
  ({ theme }) => `
  min-width: 310px;
  margin: 2px 0 0;
  padding: 0;
  position: absolute;
  list-style: none;
  background-color: #fbfbfbff;
  overflow: auto;
  max-height: 250px;
  border-radius: 4px;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transition: all 0.3s ease;

  & li {
    padding: 5px 12px;
    display: flex;
    transition: all 0.3s ease;

    & span {
      flex-grow: 1;
      transition: all 0.3s ease;
    }

    & svg {
      color: transparent;
      transition: all 0.3s ease;
    }
  }

  & li[aria-selected='true'] {
    background-color: #ff525286;
    font-weight: 700;
    transition: all 0.3s ease;

    & svg {
      var(--secundario);
      transition: all 0.3s ease;
    }
  }

  & li.${autocompleteClasses.focused} {
    background-color: #ff525286;
    cursor: pointer;
    transition: all 0.3s ease;
    & svg {
      color: currentColor;
      transition: all 0.3s ease;
    }
  }
`,
);

export default function AutocompleteList({ array, setArray, arrayPre, label, controlled = false }) {
  const opciones = React.useMemo(
    () => ordenarOpcionesPorLabel(array),
    [array]
  );
  const valorControlado = React.useMemo(
    () => Array.isArray(arrayPre) ? arrayPre : [],
    [arrayPre]
  );
  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    value,
    focused,
    setAnchorEl,
  } = useAutocomplete({
    id: 'customized-hook-demo',
    defaultValue: controlled ? undefined : arrayPre,
    value: controlled ? valorControlado : undefined,
    onChange: controlled ? (event, newValue) => setArray(newValue) : undefined,
    multiple: true,
    options: opciones,
    getOptionLabel: (option) => option.label,
    isOptionEqualToValue: (option, value) => option.value === value.value,
  });

  React.useEffect(() => {
    if (controlled) return;
    setArray(value);
  }, [controlled, value, setArray]);

  // Determinar si el input tiene valores para aplicar estilos al label
  const hasValue = value.length > 0;

  return (
    <Root>
      <div {...getRootProps()} style={{ minWidth: '100%' }}>
        <InputWrapper
          style={{ minWidth: '100%'}}
          ref={setAnchorEl}
          className={`${focused ? 'focused' : ''} ${hasValue ? 'has-value' : ''}`}
        >
          {/* Label flotante */}
          <FloatingLabel className="floating-label">
            {label}
          </FloatingLabel>

          {value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <StyledTag
                className='pop-in-scale'
                style={{ minHeight: '4vh' }}
                key={option.value}
                {...tagProps}
                label={
                  <Typography variant='subtitle1'>
                    <span>{textoMayusculas(option.label)}</span>&nbsp;
                  </Typography>
                }
              />
            );
          })}
          <input {...getInputProps()} />
        </InputWrapper>
      </div>
      {groupedOptions.length > 0 ? (
        <Listbox {...getListboxProps()}>
          {groupedOptions.map((option, index) => {
            const { key, ...optionProps } = getOptionProps({ option, index });
            return (
              <li key={option.value} {...optionProps}>
                <Typography variant='subtitle1' sx={{ pt: 1, pb: 1, pr: 0.5, pl: 0.5}}>
                  <span>{textoMayusculas(option.label)}</span>
                </Typography>
              </li>
            );
          })}
        </Listbox>
      ) : null}
    </Root>
  );
}
